import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, message, Spin, Alert, Tooltip } from 'antd';
import {
  UserOutlined, UpOutlined, DownOutlined,
  BarChartOutlined, FolderOutlined, SettingOutlined, UnorderedListOutlined,
  FullscreenOutlined, FullscreenExitOutlined, LeftOutlined, RightOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { throttle } from 'lodash';

import { getUserDetails, hasStudyViewingPermission, isValidDate } from '../../../../utils/helper';
import { TokenStorage } from '../../../../utils/cookieStorage';
import { useReportStatus } from '../../../../hooks/useReportStatus';
import { launchViewerOnDualMonitor } from '../../../../utils/MonitorManager';
import { getEditorConfig, applyTemplateToDocument, forceSaveDocument } from '../../../OnlyOfficeDocuments/documentsApi';
import useOnlyOfficeScript from '../../../OnlyOfficeDocuments/useOnlyOfficeScript';
import TemplatePanel from '../../../OnlyOfficeDocuments/TemplatePanel';

import { useReportDataOodocs } from '../../hooks/useReportDataOodocs';
import { useReportActionsOodocs } from '../../hooks/useReportActionsOodocs';
import { useReportSession } from '../../hooks/useReportSession';
import { REPORT_CONFIG } from '../../constants';
import type { OrderDetails, OoReport, Report } from '../../types';

import StatusActions from './StatusActions';
import StudyQueue from '../StudyQueue/StudyQueue';
import ReportHistory from '../Panels/ReportHistory';
import PriorReports from '../Panels/PriorReports';
import AdvancedOptions from '../Panels/AdvancedOptions';
import CriticalFindingDialog from '../Dialogs/CriticalFindingDialog';
import TemplateLoadDialog from '../Dialogs/TemplateLoadDialog';
import ViewNotesModal from '../Dialogs/ViewNotesModal';

import '../../styles/editor.css';

// Module-scope leave latch: unlike a ref, this survives component REMOUNTS within the
// same page instance (e.g. dev hot-reload, parent re-keying), so a remount during the
// close countdown cannot resurrect an editor. Keyed by order id so advancing to the
// NEXT queued study re-arms normally.
let pageLeavingOrderId: number | null = null;

interface OOReportEditorProps {
  patientDetails: OrderDetails | null;
  /** Studies queue rendered at the top of the left rail */
  orders: OrderDetails[];
  selectedIndex: number;
  queueLoading?: boolean;
  onSelectOrder: (order: OrderDetails, index: number) => void;
  handlePostSaving?: (status: string, patDetails: OrderDetails) => void;
  cancel: () => void;
  focusMode?: boolean;
  onToggleFocusMode?: () => void;
}

const OOReportEditor: React.FC<OOReportEditorProps> = ({
  patientDetails, orders, selectedIndex, queueLoading = false, onSelectOrder,
  handlePostSaving, cancel, focusMode = false, onToggleFocusMode,
}) => {
  const editorInstanceRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastActivityRef = useRef(Date.now());
  // true when the doc was auto-created in this session (no prior content)
  const isNewDocRef = useRef(false);
  // True from the moment a commit hands off to close/advance until the next study loads.
  // Blocks any editor (re)creation so no OnlyOffice beforeunload guard exists at unload.
  const leavingRef = useRef(false);
  // Resolver for an in-flight downloadAs() capture (re-signoff content extraction)
  const downloadAsResolverRef = useRef<((url: string | null) => void) | null>(null);
  // Docs whose first autosave already triggered a history refresh
  const autoStubFlushedRef = useRef<Set<number>>(new Set());
  // Latest values for the stable OO event handlers (registered once per editor init)
  const editorStateRef = useRef<{ currentReport: OoReport | null; activeDocId: number | null; refetch: (opts?: any) => void }>({
    currentReport: null,
    activeDocId: null,
    refetch: () => {},
  });

  const [activeDocId, setActiveDocId] = useState<number | null>(null);
  // Force the OO editor into read-only for the active doc (historical versions of a signed order)
  const [docViewOnly, setDocViewOnly] = useState(false);
  // Status code of the commit currently in flight (spinner on that button, others locked)
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);
  const [onlyOfficeUrl, setOnlyOfficeUrl] = useState<string | null>(null);
  const [editorConfig, setEditorConfig] = useState<any>(null);
  const [proxyDraftUser, setProxyDraftUser] = useState<string | null>(null);
  const [proxySignoffUser, setProxySignoffUser] = useState<string | null>(null);
  const [cosigningDoctor, setCosigningDoctor] = useState<string | null>(null);
  const [diagnosed, setDiagnosed] = useState<string | null>(null);
  const [correlated, setCorrelated] = useState<string | null>(null);
  const [criticalFindingVisible, setCriticalFindingVisible] = useState(false);
  const [isPageIdle, setIsPageIdle] = useState(false);
  const [studiesCollapsed, setStudiesCollapsed] = useState(false);
  const [detailsCollapsed, setDetailsCollapsed] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);

  const [expandedSections, setExpandedSections] = useState({
    reportsHistory: true,
    priorReports: false,
    advancedOptions: true,
  });

  const pacs_order = patientDetails?.pacs_order;
  const patient = pacs_order?.patient;

  // ── Populate diagnosed/correlated from study-report-details ──
  useEffect(() => {
    const d = patientDetails?.ow_diagnosed;
    const c = patientDetails?.ow_correlated;
    setDiagnosed(d === true ? 'diagnosed' : d === false ? 'notdiagnosed' : null);
    setCorrelated(c === true ? 'correlated' : c === false ? 'notcorrelated' : null);
  }, [patientDetails?.ow_diagnosed, patientDetails?.ow_correlated]);

  // ── Hooks ──
  const { statusList, loading: statusLoading } = useReportStatus();
  const {
    reports, currentReport, setCurrentReport,
    priorReports, radUsers, allUsers,
    isSignedOff, initialFetchDone, refetchReports,
  } = useReportDataOodocs(patientDetails);

  // Is THIS order in the middle of a close/advance handoff? Combines the ref (fast,
  // same-mount) with the module latch (survives remounts).
  const isLeaving = useCallback(() => {
    return leavingRef.current ||
      (pageLeavingOrderId !== null && pageLeavingOrderId === pacs_order?.pacs_ord_id);
  }, [pacs_order?.pacs_ord_id]);

  const markLeaving = useCallback(() => {
    leavingRef.current = true;
    pageLeavingOrderId = pacs_order?.pacs_ord_id ?? null;
  }, [pacs_order?.pacs_ord_id]);

  // ── Editor cleanup helper ──
  const destroyEditor = useCallback(() => {
    // Detach-first: api.js's destroyEditor() tears the frame down by NAVIGATING it,
    // which fires the iframe's unsaved-changes beforeunload and makes Chrome raise a
    // blocking "Leave site?" dialog mid-call (always dirty on the re-signoff surface,
    // where autosave is off by design). Detaching the frame from the DOM instead
    // discards its document WITHOUT firing beforeunload — no dialog can appear. The
    // detach must RESTORE the container div in the frame's place: DocEditor replaced
    // it on mount, and a later DocEditor silently no-ops (spinner forever) when
    // getElementById can't find its placeholder id. The document server still saves
    // on session end (websocket disconnect → assembly → callback).
    try {
      document.querySelectorAll('iframe[name="frameEditor"]').forEach(f => {
        const placeholder = containerRef.current;
        if (placeholder && !placeholder.isConnected && f.parentNode) {
          f.parentNode.replaceChild(placeholder, f);
        } else {
          f.remove();
        }
      });
    } catch {}
    if (editorInstanceRef.current) {
      // Frame is already gone — this only cleans api.js's internal registry.
      try { editorInstanceRef.current.destroyEditor(); } catch {}
      editorInstanceRef.current = null;
    }
  }, []);

  const onSaved = useCallback((status: string) => {
    message.success(status === 'SIGNEDOFF' ? 'Report signed off' : 'Updated successfully');
    // Every committed status (Draft / Reviewed / Sign off / re-sign-off) completes work
    // on this study: close the window (single-study queue) or advance to the next item.
    // Tear the OO editor down and do NOT refetch — the refetch would reselect the new
    // report and resurrect a fresh editor during the close countdown, whose unsaved-
    // changes beforeunload guard pops the browser's "Leave site?" prompt. Content is
    // safe: destroying the editor ends the session (the document server assembles and
    // saves it server-side) and the fork/sign-off paths flush or capture before copying.
    if (handlePostSaving) {
      markLeaving();
      destroyEditor();
      handlePostSaving(status, patientDetails);
    } else {
      refetchReports();
    }
  }, [refetchReports, handlePostSaving, patientDetails, destroyEditor, markLeaving]);

  const { saveReport, deleteReport, copyPriorReport, createNewDocument, saveAsDraft, saveAsReviewed } = useReportActionsOodocs({
    orderDetails: patientDetails,
    currentReport,
    activeDocId,
    isSignedOff,
    onSaved,
  });

  // Effective order state derived from committed reports (private autosaves don't count).
  // Drives forward-only button gating: SCANNED → DRAFTED → REVIEWED → SIGNEDOFF.
  const orderReportState = useMemo(() => {
    const statuses = reports.map(r => r.pr_status).filter(Boolean);
    if (statuses.includes('SIGNEDOFF')) return 'SIGNEDOFF';
    if (statuses.includes('REVIEWED')) return 'REVIEWED';
    if (statuses.includes('DRAFTED')) return 'DRAFTED';
    return 'SCANNED';
  }, [reports]);

  useReportSession(pacs_order?.pacs_ord_id, !isPageIdle);

  const { loaded: scriptLoaded, error: scriptError, retry: retryScript } = useOnlyOfficeScript(onlyOfficeUrl);

  // Keep the OO event handlers' view of the world current (they are registered once
  // per editor init and would otherwise close over stale state).
  useEffect(() => {
    editorStateRef.current = { currentReport, activeDocId, refetch: refetchReports };
  }, [currentReport, activeDocId, refetchReports]);

  // First autosave of a fresh AUTO-SAVED doc: flush it to storage and refresh the
  // history so the private auto-saved entry appears while typing (old-app behaviour).
  const handleEditorContentSaved = useCallback(() => {
    const { currentReport: rep, activeDocId: docId, refetch } = editorStateRef.current;
    if (!docId) return;
    const isAutoStub = !rep?.pr_status || rep.pr_status === 'AUTOSAVED';
    if (!isAutoStub || autoStubFlushedRef.current.has(docId)) return;
    autoStubFlushedRef.current.add(docId);
    forceSaveDocument(docId)
      .catch(() => { autoStubFlushedRef.current.delete(docId); })
      .finally(() => refetch());
  }, []);

  /**
   * Capture the CURRENT in-session content as a temporary URL via the editor's
   * downloadAs API. Used when re-signing a finalized report: the snapshot on disk is
   * immutable, so the live session is the only source of the user's edits.
   */
  const requestCurrentDocUrl = useCallback((): Promise<string | null> => {
    return new Promise(resolve => {
      const editor = editorInstanceRef.current;
      if (!editor || typeof editor.downloadAs !== 'function') {
        resolve(null);
        return;
      }
      const timer = setTimeout(() => {
        downloadAsResolverRef.current = null;
        resolve(null);
      }, 10_000);
      downloadAsResolverRef.current = (url: string | null) => {
        clearTimeout(timer);
        resolve(url);
      };
      try {
        editor.downloadAs('docx');
      } catch {
        clearTimeout(timer);
        downloadAsResolverRef.current = null;
        resolve(null);
      }
    });
  }, []);

  // ── Set active doc from current report ──
  useEffect(() => {
    if (!initialFetchDone || isLeaving()) return; // wait for first fetch; never during close/advance

    const doc = currentReport?.document;
    if (doc?.doc_id) {
      setDocViewOnly(false);
      setActiveDocId(doc.doc_id);
    } else if (currentReport === null && reports.length === 0) {
      // No reports exist yet — auto-create a document. The backend links it to a private
      // AUTOSAVED row; the order stays SCANNED until the user explicitly clicks Draft.
      createNewDocument().then(docId => {
        if (docId) {
          isNewDocRef.current = true;
          setDocViewOnly(false);
          setActiveDocId(docId);
          refetchReports();
        }
      });
    }
  }, [currentReport?.pr_id, initialFetchDone]);

  // ── Fetch OnlyOffice editor config when activeDocId changes ──
  const loadEditorConfig = useCallback(async (docId: number, viewOnly = false) => {
    setEditorLoading(true);
    setEditorError(null);
    setEditorConfig(null);

    try {
      const res = await getEditorConfig(docId, viewOnly ? 'view' : 'edit');
      const { config, onlyOfficeUrl: url } = res.data.data;
      setEditorConfig(config);
      setOnlyOfficeUrl(url);
    } catch (err: any) {
      setEditorError(err.response?.data?.message || 'Failed to load editor configuration');
      setEditorLoading(false);
    }
  }, []);

  // A DIFFERENT study arriving (queue advance) re-arms the editor lifecycle. A remount
  // of the same order during the close countdown does NOT (module latch still set).
  useEffect(() => {
    leavingRef.current = false;
    if (pageLeavingOrderId !== null && pageLeavingOrderId !== pacs_order?.pacs_ord_id) {
      pageLeavingOrderId = null;
    }
  }, [patientDetails]);

  useEffect(() => {
    if (!activeDocId || isLeaving()) return;
    destroyEditor();
    loadEditorConfig(activeDocId, docViewOnly);
  }, [activeDocId, docViewOnly]);

  // ── Initialize OnlyOffice editor once script + config are ready ──
  useEffect(() => {
    if (!scriptLoaded || !editorConfig || !containerRef.current) return;
    // Never (re)create an editor while closing/advancing — a live OO instance at unload
    // time pops the browser's "Leave site?" prompt.
    if (isLeaving()) return;

    const timer = setTimeout(() => {
      if (isLeaving()) return;
      try {
        editorInstanceRef.current = new (window as any).DocsAPI.DocEditor(
          'oo-report-editor-container',
          {
            ...editorConfig,
            events: {
              ...editorConfig.events,
              onDocumentReady: () => setEditorLoading(false),
              onError: (event: any) => {
                console.error('OnlyOffice editor error:', event);
                message.error('Editor encountered an error');
                setEditorLoading(false);
              },
              // data === false → the session's changes reached the document server
              onDocumentStateChange: (event: any) => {
                if (event?.data === false) handleEditorContentSaved();
              },
              // Answer to our downloadAs() call — URL of the assembled current content
              onDownloadAs: (event: any) => {
                const url = event?.data?.url || (typeof event?.data === 'string' ? event.data : null);
                downloadAsResolverRef.current?.(url);
                downloadAsResolverRef.current = null;
              },
            },
          },
        );
      } catch (err) {
        console.error('Failed to initialize OnlyOffice editor:', err);
        setEditorError('Failed to initialize editor');
        setEditorLoading(false);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [scriptLoaded, editorConfig]);

  // Cleanup on unmount
  useEffect(() => () => destroyEditor(), []);

  // ── Idle detection (same as ReportEditor) ──
  useEffect(() => {
    const IDLE_TIME = 60 * 1000;

    const resetIdleTimer = () => {
      lastActivityRef.current = Date.now();
      if (isPageIdle) setIsPageIdle(false);
    };

    const throttledReset = throttle(resetIdleTimer, 1000, { leading: true, trailing: false });

    const checkIdle = () => {
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= IDLE_TIME && !isPageIdle) setIsPageIdle(true);
      else if (elapsed < IDLE_TIME && isPageIdle) setIsPageIdle(false);
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(e => document.addEventListener(e, throttledReset, true));
    const interval = setInterval(checkIdle, 30_000);

    return () => {
      events.forEach(e => document.removeEventListener(e, throttledReset, true));
      clearInterval(interval);
    };
  }, [isPageIdle]);

  // ── OnlyOffice dictation plugin integration ──
  useEffect(() => {
    const handlePluginMessage = (event: MessageEvent) => {
      if (event.data?.type === 'pacs_request_dictation_token') {
        try {
          const token = TokenStorage.getAccessToken();
          if (token && event.source) {
            (event.source as any).postMessage(
              { type: 'pacs_dictation_token', token },
              '*'
            );
          }
        } catch (error) {
          console.error('Failed to send dictation token:', error);
        }
      }
    };

    window.addEventListener('message', handlePluginMessage);

    return () => {
      window.removeEventListener('message', handlePluginMessage);
    };
  }, []);

  // ── Handlers ──
  const proxyMode = proxyDraftUser ? 'draft' : proxySignoffUser ? 'signoff' : null;

  const handleSaveAsDraft = useCallback(async () => {
    const newDocId = await saveAsDraft(proxyDraftUser ?? undefined);
    if (newDocId) {
      if (handlePostSaving) {
        // Drafting completes work on this study — close / advance the queue.
        // (Editor torn down first so its beforeunload guard can't block the close.)
        markLeaving();
        destroyEditor();
        handlePostSaving('DRAFTED', patientDetails);
      } else {
        // Standalone fallback: switch the editor to the forked draft.
        setDocViewOnly(false);
        setActiveDocId(newDocId);
        refetchReports({ selectDocId: newDocId });
      }
    }
  }, [saveAsDraft, proxyDraftUser, refetchReports, handlePostSaving, patientDetails, destroyEditor]);

  const handleSaveForm = useCallback((statusCode: string) => {
    if (pendingAction) return; // an action is already in flight

    // For sign-off, both diagnosed and correlated must be selected
    if (statusCode === 'SIGNEDOFF' && (!diagnosed || !correlated)) {
      // Make sure the user can see the controls they need to fill in
      setExpandedSections(prev => ({ ...prev, advancedOptions: true }));
      message.error('Please select both "Clinically Diagnosed" and "Clinically Correlated" before signing off');
      return;
    }

    // Reviewed forks a NEW REVIEWED entry with the current content, then completes work
    // on this study (close / advance). If the study is reopened later, the reviewed copy
    // is the newest editable entry and becomes the autosave target.
    // (Draft is locked once the order is REVIEWED; the workflow never regresses.)
    if (statusCode === 'REVIEWED') {
      setPendingAction('REVIEWED');
      saveAsReviewed(proxyDraftUser ?? undefined).then(newDocId => {
        if (newDocId) {
          if (handlePostSaving) {
            markLeaving();
            destroyEditor();
            handlePostSaving('REVIEWED', patientDetails);
          } else {
            setDocViewOnly(false);
            setActiveDocId(newDocId);
            refetchReports({ selectDocId: newDocId });
          }
        }
      }).finally(() => setPendingAction(null));
      return;
    }

    // Draft: first click promotes the private auto-saved entry in place (order goes
    // SCANNED → DRAFTED, the entry becomes a shared draft); further clicks fork a new
    // draft entry with the current content. Either way the study is then complete for
    // this sitting — the queue closes / advances (promote via onSaved, fork below).
    if (statusCode === 'DRAFTED') {
      const isAutoStub = !currentReport?.pr_status || currentReport.pr_status === 'AUTOSAVED';
      setPendingAction('DRAFTED');
      if (!isAutoStub) {
        handleSaveAsDraft().finally(() => setPendingAction(null));
        return;
      }
      saveReport('DRAFTED', { proxy_user: proxyDraftUser, co_signing_doctor: cosigningDoctor })
        .finally(() => setPendingAction(null));
      return;
    }

    const proxyUser = statusCode === 'SIGNEDOFF' ? proxySignoffUser : proxyDraftUser;

    // Convert radio button values to boolean
    const payload: any = {
      proxy_user: proxyUser,
      co_signing_doctor: cosigningDoctor,
    };

    if (diagnosed) {
      payload.diagnosed = diagnosed === 'diagnosed';
    }
    if (correlated) {
      payload.correlated = correlated === 'correlated';
    }

    // Re-signing a finalized report: the snapshot on disk is immutable and its session
    // never autosaves, so extract the live in-session content via downloadAs and send
    // its URL along — the backend downloads it as the source of the new signed report.
    // Only capture when the signed doc is what's actually open in the editor (not a
    // read-only historical version) — otherwise sign the snapshot as-is.
    setPendingAction(statusCode);
    if (
      statusCode === 'SIGNEDOFF' &&
      currentReport?.document?.doc_status === 'finalized' &&
      !docViewOnly &&
      activeDocId === currentReport.document.doc_id
    ) {
      requestCurrentDocUrl()
        .then(amendUrl => saveReport(statusCode, amendUrl ? { ...payload, amend_file_url: amendUrl } : payload))
        .finally(() => setPendingAction(null));
      return;
    }

    saveReport(statusCode, payload).finally(() => setPendingAction(null));
  }, [saveReport, saveAsReviewed, handleSaveAsDraft, requestCurrentDocUrl, refetchReports, handlePostSaving, patientDetails, destroyEditor, pendingAction, currentReport, activeDocId, docViewOnly, proxyDraftUser, proxySignoffUser, cosigningDoctor, diagnosed, correlated]);

  const handleLoadReport = useCallback((report: Report) => {
    const ooReport = report as OoReport;
    const docId = ooReport.document?.doc_id;
    if (!docId) return;

    if (ooReport.document?.doc_status === 'finalized') {
      // The signed snapshot opens in edit mode as the re-reporting surface. Nothing the
      // user types persists server-side — only another Sign off snapshots the edits.
      setCurrentReport(ooReport);
      setDocViewOnly(false);
      setActiveDocId(docId);
    } else if (orderReportState === 'SIGNEDOFF') {
      // Once the order is signed, historical drafts/reviews are read-only records.
      // Keep the signed report as the action context — only the viewer doc changes.
      setDocViewOnly(true);
      setActiveDocId(docId);
    } else {
      // Switch the editing context (and autosave target) to this entry.
      setCurrentReport(ooReport);
      setDocViewOnly(false);
      setActiveDocId(docId);
    }
  }, [setCurrentReport, orderReportState]);

  const handleLoadPriorReport = useCallback(async (report: Report) => {
    const ooReport = report as OoReport;
    const newDocId = await copyPriorReport(ooReport);
    if (newDocId) {
      setDocViewOnly(false);
      setActiveDocId(newDocId);
      refetchReports({ selectDocId: newDocId });
    }
  }, [copyPriorReport, refetchReports]);

  // ── Apply template to current document (backend handles append/replace, then reload editor) ──
  const [pendingTemplateId, setPendingTemplateId] = useState<number | null>(null);
  const [templateLoadDialogVisible, setTemplateLoadDialogVisible] = useState(false);

  const handleInsertTemplate = useCallback((templateId: number) => {
    if (!activeDocId) {
      message.error('No active document');
      return;
    }
    setPendingTemplateId(templateId);
    // Doc was just created in this session (no prior content) — auto-replace without asking
    if (isNewDocRef.current) {
      applyTemplate(templateId, 'replace');
    } else {
      setTemplateLoadDialogVisible(true);
    }
  }, [activeDocId]);

  const applyTemplate = useCallback(async (templateId: number, action: 'append' | 'replace') => {
    if (!activeDocId || !templateId) return;
    setTemplateLoadDialogVisible(false);
    try {
      await applyTemplateToDocument(activeDocId, templateId, action);
      isNewDocRef.current = false;
      destroyEditor();
      loadEditorConfig(activeDocId);
      message.success(`Template ${action === 'append' ? 'appended' : 'applied'}`);
    } catch (err: any) {
      console.error('Failed to apply template:', err);
      message.error(err.response?.data?.message || 'Failed to apply template');
    } finally {
      setPendingTemplateId(null);
    }
  }, [activeDocId, destroyEditor, loadEditorConfig]);

  const handleDeleteReport = useCallback(async (report: Report) => {
    const ooReport = report as OoReport;
    const isLastReport = reports.length <= 1;
    const deleted = await deleteReport(ooReport);
    if (!deleted) return;
    if (ooReport.document?.doc_id === activeDocId) {
      destroyEditor();
      setActiveDocId(null);
    }
    if (isLastReport) {
      if (handlePostSaving) markLeaving();
      destroyEditor();
      handlePostSaving?.('DELETED', patientDetails);
    } else {
      refetchReports();
    }
  }, [deleteReport, refetchReports, activeDocId, destroyEditor, reports.length, handlePostSaving, patientDetails]);

  const handleCosigning = useCallback((val: string, _userData: any) => {
    setCosigningDoctor(val);
  }, []);

  const toggleSection = (key: string) =>
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  // ── Render ──
  return (
    <div className={`editor-container${focusMode ? ' focus-mode' : ''}`}>
      {/* ── Pane 1: Studies ── */}
      <aside className={`report-pane pane-studies${studiesCollapsed ? ' collapsed' : ''}`}>
        {studiesCollapsed ? (
          <Tooltip title="Show studies" placement="right">
            <button type="button" className="rail-expand-btn" onClick={() => setStudiesCollapsed(false)} aria-label="Expand studies panel">
              <RightOutlined />
            </button>
          </Tooltip>
        ) : (
          <>
            <div className="rail-header">
              <span className="rail-header-title">
                <UnorderedListOutlined /> Studies{orders.length ? ` · ${orders.length}` : ''}
              </span>
              <Tooltip title="Hide panel" placement="right">
                <button type="button" className="rail-icon-btn" onClick={() => setStudiesCollapsed(true)} aria-label="Collapse studies panel">
                  <LeftOutlined />
                </button>
              </Tooltip>
            </div>
            <div className="rail-scroll">
              <StudyQueue orders={orders} selectedIndex={selectedIndex} loading={queueLoading} onSelect={onSelectOrder} />
            </div>
          </>
        )}
      </aside>

      {/* ── Pane 2: Study details ── */}
      <aside className={`report-pane pane-details${detailsCollapsed ? ' collapsed' : ''}`}>
        {detailsCollapsed ? (
          <Tooltip title="Show study details" placement="right">
            <button type="button" className="rail-expand-btn" onClick={() => setDetailsCollapsed(false)} aria-label="Expand details panel">
              <RightOutlined />
            </button>
          </Tooltip>
        ) : (
          <>
            <div className="rail-header">
              <span className="rail-header-title">
                <UserOutlined /> Study Details
              </span>
              <Tooltip title="Hide panel" placement="right">
                <button type="button" className="rail-icon-btn" onClick={() => setDetailsCollapsed(true)} aria-label="Collapse details panel">
                  <LeftOutlined />
                </button>
              </Tooltip>
            </div>
            <div className="rail-scroll">
              {patient ? (
                <>
                  {/* Patient summary */}
                  <div className="patient-card">
                    <div className="patient-card-name">
                      <UserOutlined className="patient-card-icon" />
                      <span>{patient?.pat_name}{patient?.pat_pin ? `, ${patient.pat_pin}` : ''}</span>
                    </div>
                    <div className="patient-card-meta">
                      {patient?.pat_sex || '-'} · {patient?.pat_dob && isValidDate(patient.pat_dob) ? moment(patient.pat_dob).fromNow(true) : '-'} · {pacs_order?.po_modality || '-'}
                    </div>
                    <div className="patient-card-meta">Ref: {pacs_order?.po_ref_doc || '-'}</div>
                    <div className="patient-card-actions">
                      {hasStudyViewingPermission(getUserDetails()) && (
                        <Button
                          className="rail-action-btn"
                          size="small"
                          onClick={async () => {
                            const w = await launchViewerOnDualMonitor(patientDetails!.ps_study_uid, patientDetails!);
                            if (!w) message.warning('Could not open viewer window');
                          }}
                        >
                          Launch Viewer
                        </Button>
                      )}
                      <Button
                        className="rail-action-btn"
                        size="small"
                        icon={<FolderOutlined />}
                        onClick={() => setNotesModalOpen(true)}
                      >
                        View Notes
                      </Button>
                    </div>
                  </div>

                  {/* Reports History */}
                  <div className="collapsible-section">
                    <div className="collapsible-header" onClick={() => toggleSection('reportsHistory')}>
                      <div className="collapsible-header-left"><BarChartOutlined className="collapsible-icon" /><span className="collapsible-title">Reports History</span></div>
                      {expandedSections.reportsHistory ? <UpOutlined className="collapsible-arrow" /> : <DownOutlined className="collapsible-arrow" />}
                    </div>
                    {expandedSections.reportsHistory && (
                      <div className="collapsible-content" style={{ paddingTop: 0, paddingBottom: 0 }}>
                        <ReportHistory
                          reports={reports}
                          activeDocId={activeDocId}
                          orderSignedOff={orderReportState === 'SIGNEDOFF'}
                          onLoad={handleLoadReport}
                          onDelete={handleDeleteReport}
                        />
                      </div>
                    )}
                  </div>

                  {/* Prior Reports */}
                  {priorReports.length > 0 && (
                    <div className="collapsible-section">
                      <div className="collapsible-header" onClick={() => toggleSection('priorReports')}>
                        <div className="collapsible-header-left"><BarChartOutlined className="collapsible-icon" /><span className="collapsible-title">Prior Reports</span></div>
                        {expandedSections.priorReports ? <UpOutlined className="collapsible-arrow" /> : <DownOutlined className="collapsible-arrow" />}
                      </div>
                      {expandedSections.priorReports && (
                        <div className="collapsible-content" style={{ paddingTop: 0, paddingBottom: 0 }}>
                          <PriorReports reports={priorReports} isSignedOff={isSignedOff} onLoadAsTemplate={handleLoadPriorReport} />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Advanced Options */}
                  {REPORT_CONFIG.SHOW_ADVANCED_OPTIONS && (
                    <div className="collapsible-section">
                      <div className="collapsible-header" onClick={() => toggleSection('advancedOptions')}>
                        <div className="collapsible-header-left"><SettingOutlined className="collapsible-icon" /><span className="collapsible-title">Advanced Options</span></div>
                        {expandedSections.advancedOptions ? <UpOutlined className="collapsible-arrow" /> : <DownOutlined className="collapsible-arrow" />}
                      </div>
                      {expandedSections.advancedOptions && (
                        <AdvancedOptions
                          radUsers={radUsers}
                          allUsers={allUsers}
                          onNotifyPhysician={() => setCriticalFindingVisible(true)}
                          onCosignChange={handleCosigning}
                          isSignedOff={isSignedOff}
                          currentUsername={getUserDetails()?.username}
                          onProxyDraftChange={(val) => { setProxyDraftUser(val); setProxySignoffUser(null); }}
                          onProxySignoffChange={(val) => { setProxySignoffUser(val); setProxyDraftUser(null); }}
                          diagnosed={diagnosed}
                          correlated={correlated}
                          onDiagnosedChange={setDiagnosed}
                          onCorrelatedChange={setCorrelated}
                        />
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="rail-empty">Select a study to begin</div>
              )}
            </div>
          </>
        )}
      </aside>

      {/* ── Editor (max space) ── */}
      <div className="editor-section">
        <div className="section-header">
          <div className="section-header-title">Medical Report</div>
          {docViewOnly ? (
            <span style={{ marginLeft: 12, fontSize: 12, color: '#8A6A2E', background: '#F5EFE2', borderRadius: 4, padding: '2px 8px' }}>
              Read-only — historical version
            </span>
          ) : currentReport?.pr_status === 'SIGNEDOFF' ? (
            <span style={{ marginLeft: 12, fontSize: 12, color: '#00795C', background: '#DAF3EE', borderRadius: 4, padding: '2px 8px' }}>
              Signed-off report — edits are kept only if you sign off again
            </span>
          ) : null}
        </div>

        <div className="section-content" style={{ padding: 0, position: 'relative' }}>
          {/* Overlay layer — React freely adds/removes nodes here.
              It is a sibling of the OO container layer, NOT a wrapper around it,
              so React never uses any OO-mutated node as an insertBefore reference. */}
          <div className="absolute inset-0" style={{ zIndex: 10, pointerEvents: 'none' }}>
            {!activeDocId && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#45556C', pointerEvents: 'auto' }}>
                {patientDetails ? 'Loading…' : 'Select a study to begin reporting'}
              </div>
            )}
            {activeDocId && editorLoading && !scriptError && !editorError && (
              <div className="flex items-center justify-center" style={{ height: '100%', background: 'rgba(0,0,0,0.3)', pointerEvents: 'auto' }}>
                <Spin size="large" tip="Loading editor..." />
              </div>
            )}
            {activeDocId && scriptError && (
              <div className="flex items-center justify-center bg-white" style={{ height: '100%', pointerEvents: 'auto' }}>
                <Alert type="error" message="Failed to load editor" description={scriptError} action={<Button onClick={retryScript}>Retry</Button>} />
              </div>
            )}
            {activeDocId && editorError && (
              <div className="flex items-center justify-center bg-white" style={{ height: '100%', pointerEvents: 'auto' }}>
                <Alert type="error" message="Editor Error" description={editorError} action={
                  <Button onClick={() => { setEditorError(null); if (activeDocId) loadEditorConfig(activeDocId, docViewOnly); }}>Retry</Button>
                } />
              </div>
            )}
          </div>
          {/* OO container layer — this div and its children are NEVER conditional.
              React renders exactly one stable child here at all times, so it never
              calls insertBefore/removeChild on nodes that OnlyOffice has mutated. */}
          <div className="h-full w-full">
            <div id="oo-report-editor-container" ref={containerRef} style={{ height: '100%', width: '100%' }} />
          </div>
        </div>

        <div className="section-footer">
          {/* Templates write into the doc server-side — not applicable to the immutable
              signed surface or read-only historical views. */}
          {pacs_order && orderReportState !== 'SIGNEDOFF' && (
            <TemplatePanel
              pacsOrdId={pacs_order.pacs_ord_id}
              modality={pacs_order.po_modality}
              bodyPart={pacs_order.po_body_part}
              onInsertTemplate={handleInsertTemplate}
            />
          )}

          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {onToggleFocusMode && (
              <Tooltip title={`${focusMode ? 'Exit' : 'Enter'} focus mode (Ctrl+Shift+F)`}>
                <Button
                  className="footer-btn"
                  type="default"
                  icon={focusMode ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                  onClick={onToggleFocusMode}
                />
              </Tooltip>
            )}
            <Button className="footer-btn" type="default" onClick={cancel}>Cancel</Button>
            <StatusActions
              statusList={statusList}
              statusLoading={statusLoading}
              orderReportState={orderReportState}
              pendingAction={pendingAction}
              proxyMode={proxyMode}
              onAction={handleSaveForm}
            />
          </div>
        </div>
      </div>

      {/* ── Dialogs ── */}
      <TemplateLoadDialog
        visible={templateLoadDialogVisible}
        onAppend={() => applyTemplate(pendingTemplateId, 'append')}
        onReplace={() => applyTemplate(pendingTemplateId, 'replace')}
        onCancel={() => { setTemplateLoadDialogVisible(false); setPendingTemplateId(null); }}
      />
      <CriticalFindingDialog
        visible={criticalFindingVisible}
        orderDetails={patientDetails}
        onClose={() => setCriticalFindingVisible(false)}
      />
      {patientDetails && (
        <ViewNotesModal
          open={notesModalOpen}
          details={patientDetails}
          onClose={() => setNotesModalOpen(false)}
        />
      )}
    </div>
  );
};

export default OOReportEditor;
